// src/App.tsx

import { useState, useEffect } from "react";
import { Card, Result, Spin } from "antd";
import axios from "axios";
import MultiStepForm from "./components/MultiStepForm";
import PrivatePartyForm from "./components/forms/PrivatePartyForm";
import CampForm from "./components/forms/CampForm";
import EducationForm from "./components/forms/EducationForm";
import SeminarForm from "./components/forms/SeminarForm";
import GraduationForm from "./components/forms/GraduationForm";
import SuccessPage from "./components/shared/SuccessPage";

export interface FormConfig {
  uuid: string;
  name: string;
  form_template: string;
  form_enabled: boolean;
  gdpr_consent_text: string | null;
  success_title: string | null;
  success_message: string | null;
  success_redirect_url: string | null;
  enable_fb_conversion: boolean;
  cities: { uuid: string; name: string }[];
}

const apiBaseUrl = process.env.REACT_APP_API_URL;

function App() {
  const [showEndscreen, setShowendScreen] = useState<boolean>(false);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [reservationType, setReservationType] = useState<string | null>(null);
  const [formConfig, setFormConfig] = useState<FormConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Log app version for debugging
    const timestamp = Date.now();
    fetch(`/version.json?cb=${timestamp}`)
      .then(response => response.json())
      .then(data => {
        console.log('App Version:', data.version);
        console.log('Build Time:', new Date(data.buildTime).toLocaleString());
      })
      .catch(() => {
        console.log('App Version: 2.0.0 (fallback)');
      });

    // Extract reservation_type from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const reservationTypeParam = urlParams.get("reservation_type");

    // Hardcode reservation_type if not provided via URL params
    const hardcodedReservationType = "370155446c004517918c5cf360a806b4";
    const rtUuid = reservationTypeParam || hardcodedReservationType;

    setReservationType(rtUuid);

    // Fetch form configuration
    axios
      .get(`${apiBaseUrl}/api/reservation_types/form-config/${rtUuid}`)
      .then((res) => {
        // Response wrapper: res.data = { statusCode, payload: { success, data }, ... }
        const payload = res.data.payload || res.data;
        if (payload.success && payload.data) {
          setFormConfig(payload.data);
        } else {
          // Fallback to birthday if form config not found
          setFormConfig({
            uuid: rtUuid,
            name: "Vaikų gimtadieniai",
            form_template: "birthday",
            form_enabled: true,
            gdpr_consent_text: null,
            success_title: null,
            success_message: null,
            success_redirect_url: null,
            enable_fb_conversion: false,
            cities: [],
          });
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch form config:", err);
        // Fallback to birthday form
        setFormConfig({
          uuid: rtUuid,
          name: "Vaikų gimtadieniai",
          form_template: "birthday",
          form_enabled: true,
          gdpr_consent_text: null,
          success_title: null,
          success_message: null,
          success_redirect_url: null,
          enable_fb_conversion: false,
          cities: [],
        });
        setLoading(false);
      });
  }, []);

  const handleSubmitSuccess = () => {
    setShowendScreen(true);

    // Handle redirect based on form config
    const redirectUrl = formConfig?.success_redirect_url;
    const enableFb = formConfig?.enable_fb_conversion;

    if (redirectUrl || enableFb) {
      setTimeout(() => {
        const url = redirectUrl || "https://pramoguloftas.lt/gimtadieniai-vaikams/?success=orderPlaced&conversion=true";
        if (window.parent && window.parent !== window) {
          window.parent.location.href = url;
        }
      }, 3000);
    }
  };

  const renderForm = () => {
    if (!formConfig || !reservationType) return null;

    const commonProps = {
      onSubmitSuccess: handleSubmitSuccess,
      reservationType,
      formConfig,
    };

    switch (formConfig.form_template) {
      case "private_party":
        return <PrivatePartyForm {...commonProps} />;
      case "camp":
        return <CampForm {...commonProps} />;
      case "education":
        return <EducationForm {...commonProps} />;
      case "seminar":
        return <SeminarForm {...commonProps} />;
      case "graduation":
        return <GraduationForm {...commonProps} />;
      case "birthday":
      default:
        return (
          <MultiStepForm
            onSubmitSuccess={handleSubmitSuccess}
            reservationType={reservationType}
            formConfig={formConfig}
          />
        );
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: "400px", fontFamily: "Poppins, sans-serif", textAlign: "center", padding: "40px" }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: "#666" }}>Kraunama forma...</p>
      </div>
    );
  }

  if (criticalError) {
    return (
      <div style={{ maxWidth: "400px", fontFamily: "Poppins, sans-serif" }}>
        <Result
          status="error"
          title="Aptikta klaida"
          subTitle={errorMessage || "Prašome susisiekti su administratoriumi."}
        />
      </div>
    );
  }

  if (formConfig && !formConfig.form_enabled) {
    return (
      <div style={{ maxWidth: "400px", fontFamily: "Poppins, sans-serif" }}>
        <Result
          status="info"
          title="Forma laikinai neprieinama"
          subTitle="Ši rezervacijos forma šiuo metu yra išjungta."
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "400px", fontFamily: "Poppins, sans-serif" }}>
      {showEndscreen ? (
        <SuccessPage formConfig={formConfig} />
      ) : (
        renderForm()
      )}
    </div>
  );
}

export default App;
